import { useCallback, useState } from 'react'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { RequestParams } from '@/Components/Pages/Login/types'

import useCsrfToken from '@/hooks/useCsrfToken'

import TextField from '@/Components/Common/Inputs/TextField'
import Card from '@/Components/Pages/Login/Card'
import CardButton from '@/Components/Pages/Login/CardButton'
import CardHeader from '@/Components/Pages/Login/CardHeader'

const requestPasswordChange = async ({ csrfToken, user }: RequestParams) => {
  const response = await fetch('/users/password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({ user })
  })

  if (!response.ok) {
    const { error } = await response.json()
    return { error }
  }

  return { success: true }
}

type PasswordChangeCardProps = {
  returnToLogin: () => void
}

const PasswordChangeCard: React.FC<PasswordChangeCardProps> = ({
  returnToLogin
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [changed, setChanged] = useState<boolean>(false)
  const [canSubmit, setCanSubmit] = useState<boolean>(false)
  const csrfToken = useCsrfToken()

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const formData = new FormData(e.currentTarget)
      const email = formData.get('email') as string

      if (!email) {
        setError('Email is required')
        return
      }

      setLoading(true)

      const { success, error = null } = await requestPasswordChange({
        csrfToken,
        user: { email }
      })

      if (error) {
        setError(error)
      }

      if (success) {
        setChanged(true)
      }

      setLoading(false)
    },
    []
  )

  return (
    <Card error={error} success={changed ? true : undefined}>
      <Stack component="form" onSubmit={handleSubmit} gap={1.5}>
        <CardHeader
          title="Forgot password?"
          error={error}
          successMessage={
            changed
              ? 'Check your email for instructions to reset your password.'
              : undefined
          }
        />
        <Stack gap={4}>
          {!changed && (
            <Typography>
              Instructions to reset your password will be sent to your email.
            </Typography>
          )}
          <TextField
            name="email"
            type="email"
            label="Email"
            disabled={changed}
            onChange={(e) => {
              setCanSubmit(e.target.value.length > 0)
              e.target.value.length && setError(null)
            }}
          />

          <Stack gap={1} alignItems="center">
            <CardButton loading={loading} disabled={changed || !canSubmit}>
              Reset password
            </CardButton>
            {!changed && (
              <Link
                href="#"
                onClick={(e) => {
                  e?.preventDefault()
                  returnToLogin()
                }}
              >
                Return to log in
              </Link>
            )}
            {changed && (
              <CardButton
                onClick={(e) => {
                  e?.preventDefault()
                  returnToLogin()
                }}
              >
                Return to log in
              </CardButton>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  )
}

export default PasswordChangeCard
